// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// feature-components
import EnergyXplorerAlarmManagementActions from '@/features/energyxplorer/components/alarms/EnergyXplorerAlarmManagementActions';
import DeepDiveList from '@/features/energyxplorer/components/portfolio-dashboard/DeepDiveList';
import EnergyXplorerAlarmSetupCard from '@/features/energyxplorer/components/portfolio-dashboard/EnergyXplorerAlarmSetupCard';
import EnergyXplorerAlarmsGrid from '@/features/energyxplorer/components/portfolio-dashboard/EnergyXplorerAlarmsGrid';
import EnergyXplorerCard from '@/features/energyxplorer/components/sales-cards/EnergyXplorerCard';

// feature-hooks
import { useDeepDiveAlarms } from '@/features/energyxplorer/hooks/useDeepDiveAlarms';
import { useEnergyXplorerAlarmSetup } from '@/features/energyxplorer/hooks/useEnergyXplorerAlarmSetup';

// components
import { useHasService } from '@/hooks/useHasService';

// relative
import { useDeepDiveSavings } from '../../hooks/useDeepDiveSavings';
import {
  type DeepDiveAnalysisKind,
  getDeepDiveAnalysisKind,
  useDeepDiveYearOverview,
} from '../../hooks/useDeepDiveYearOverview';
import DeepDiveCountKpiCard from './DeepDiveCountKpiCard';

const KIND_ORDER: Record<DeepDiveAnalysisKind, number> = {
  power: 0,
  heat: 1,
  water: 2,
};

interface DeepDiveModuleProps {
  accountId: string | undefined;
  energyGroups: GetEnergyGroupDto[] | undefined;
  hasAnalyticsService: boolean;
}

const DeepDiveModule = ({
  accountId,
  energyGroups,
  hasAnalyticsService,
}: DeepDiveModuleProps) => {
  const { t } = useTranslation('energy-analysis');
  const theme = useTheme();
  const hasLoadedEnergyGroups = energyGroups !== undefined;

  const filteredEnergyGroups = useMemo(
    () =>
      energyGroups
        ?.filter((group) => !group.type?.toLowerCase().includes('portfolio'))
        .sort(
          (a, b) =>
            KIND_ORDER[getDeepDiveAnalysisKind(a.type)] -
            KIND_ORDER[getDeepDiveAnalysisKind(b.type)],
        ),
    [energyGroups],
  );

  const { alarms, isLoading: isAlarmsLoading } = useDeepDiveAlarms({
    energyGroups: filteredEnergyGroups,
    accountId: accountId ? Number(accountId) : undefined,
    enabled: hasAnalyticsService,
  });
  const {
    shouldShowSetupPrompt,
    isLoading: isAlarmSetupLoading,
    isEnabling,
    enableAlarms,
    autoCreatedAlarmGroup,
    canDeleteAutoCreatedAlarmGroup,
    isDeletingAutoCreatedAlarmGroup,
    deleteAutoCreatedAlarmGroup,
  } = useEnergyXplorerAlarmSetup({
    accountId: accountId ? Number(accountId) : undefined,
    enabled: hasAnalyticsService,
  });

  const { savingsByGroupId, isLoading: isSavingsLoading } = useDeepDiveSavings({
    energyGroups: filteredEnergyGroups,
    accountId: accountId ? Number(accountId) : undefined,
  });
  const {
    consumptionByGroupId,
    co2eKgByGroupId,
    totalsByKind,
    isLoading: isOverviewLoading,
  } = useDeepDiveYearOverview({
    energyGroups: filteredEnergyGroups,
    accountId: accountId ? Number(accountId) : undefined,
  });
  const { hasService, isLoading } = useHasService('EnergyXplorer');
  const isAlarmModuleLoading =
    hasAnalyticsService &&
    (!hasLoadedEnergyGroups || isAlarmsLoading || isAlarmSetupLoading);
  const shouldRenderSetupPrompt =
    !isAlarmModuleLoading && shouldShowSetupPrompt;

  if (isLoading) return <CircularProgress />;

  if (!hasService && !isLoading) return <EnergyXplorerCard />;

  return (
    <Box>
      {/* Module Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1,
        }}
      >
        <Box flex={1}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {t('deepdive.module.title')}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DeepDiveCountKpiCard
              title={t('deepdive.cards.power.title')}
              subtitle={t('deepdive.cards.power.subtitle')}
              value={totalsByKind.power.consumption}
              unit="kWh"
              secondaryLabel={t('deepdive.cards.co2e_label')}
              secondaryValue={totalsByKind.power.co2eKg}
              secondaryUnit={t('deepdive.cards.co2e_year.unit.co2e')}
              sparklineData={totalsByKind.power.series}
              color={theme.palette.power?.main ?? theme.palette.primary.main}
              icon={<BoltRoundedIcon />}
              isLoading={isOverviewLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DeepDiveCountKpiCard
              title={t('deepdive.cards.heat.title')}
              subtitle={t('deepdive.cards.heat.subtitle')}
              value={totalsByKind.heat.consumption}
              unit="kWh"
              sparklineData={totalsByKind.heat.series}
              color={theme.palette.heat?.main ?? theme.palette.error.main}
              icon={<LocalFireDepartmentRoundedIcon />}
              isLoading={isOverviewLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DeepDiveCountKpiCard
              title={t('deepdive.cards.water.title')}
              subtitle={t('deepdive.cards.water.subtitle')}
              value={totalsByKind.water.consumption}
              unit="m³"
              sparklineData={totalsByKind.water.series}
              color={theme.palette.water?.main ?? theme.palette.info.main}
              icon={<WaterDropRoundedIcon />}
              isLoading={isOverviewLoading}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={12}>
            <DeepDiveList
              energyGroups={filteredEnergyGroups}
              accountId={accountId}
              savingsByGroupId={savingsByGroupId}
              isSavingsLoading={isSavingsLoading}
              consumptionYearByGroupId={consumptionByGroupId}
              isConsumptionYearLoading={isOverviewLoading}
              co2eByGroupId={co2eKgByGroupId}
              isCo2eLoading={isOverviewLoading}
            />
          </Grid>
        </Grid>

        {hasAnalyticsService ? (
          shouldRenderSetupPrompt ? (
            <EnergyXplorerAlarmSetupCard
              accountId={accountId}
              title={t('deepdive.alarms_list.title')}
              onEnable={enableAlarms}
              isLoading={isEnabling}
              autoCreatedAlarmGroupName={autoCreatedAlarmGroup?.Name}
              canDeleteAutoCreatedAlarmGroup={canDeleteAutoCreatedAlarmGroup}
              isDeletingAutoCreatedAlarmGroup={isDeletingAutoCreatedAlarmGroup}
              onDeleteAutoCreatedAlarmGroup={deleteAutoCreatedAlarmGroup}
            />
          ) : (
            <EnergyXplorerAlarmsGrid
              accountId={accountId}
              alarms={alarms}
              isLoading={isAlarmModuleLoading}
              emptyText={t('deepdive.alarms_list.empty')}
              title={t('deepdive.alarms_list.title')}
              toolbarActions={
                <EnergyXplorerAlarmManagementActions
                  accountId={accountId}
                  autoCreatedAlarmGroupName={autoCreatedAlarmGroup?.Name}
                  canDeleteAutoCreatedAlarmGroup={
                    canDeleteAutoCreatedAlarmGroup
                  }
                  isDeletingAutoCreatedAlarmGroup={
                    isDeletingAutoCreatedAlarmGroup
                  }
                  onDeleteAutoCreatedAlarmGroup={deleteAutoCreatedAlarmGroup}
                />
              }
            />
          )
        ) : null}
      </Stack>
    </Box>
  );
};

export default DeepDiveModule;
